/**
 * Step 3 — who fills each role. A role either varies per recipient, and so
 * becomes a column in the recipient list, or is the same person on every copy
 * in the batch.
 *
 * This is the one genuinely new idea in the flow: every competitor makes the
 * sender repeat a counter-signer in all 500 CSV rows, where a typo on row 300
 * quietly routes one envelope to the wrong person.
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import CustomContactNumberInput from "global/components/CustomContactNumberInput/CustomContactNumberInput";
import strings from "global/constants/StringConstants";
import approversAndSignersStyles from "screens/SignRequest/ApproversAndSigners.styles";
import recipientsTokens from "screens/SignRequest/ApproversAndSigners.tokens";
import {
  CaretDownGlyph,
  ShieldGlyph,
} from "screens/SignRequest/components/RecipientsIcons";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import useBulkGates from "../useBulkGates";
import { useIdvSecurityOptions } from "hooks/use-idv-security-options";
import {
  IDV_SECURITY_OPTIONS,
  isIdvSecurityKey,
} from "helpers/identityVerificationHelpers";
import BulkDelaySlot from "./BulkDelaySlot";
import { bulkShellStyles, rolesStyles } from "../BulkSend.styles";
import tokens from "../BulkSend.tokens";
import {
  BULK_CHANNEL_LABELS,
  BULK_CHANNEL_OPTIONS,
  BULK_ACCESS_CODE_MAX,
  BULK_SECURITY_LABELS,
  BulkDeliveryChannel,
  BulkRoleAssignment,
  BulkRoleErrors,
  BulkRoleMode,
  BulkSecurityKind,
  roleNeedsPhone,
} from "../BulkSendTypesAndValidation";
import {
  fieldNote,
  recipientCountLabel,
  roleColor,
  roleInitials,
} from "../BulkSendHelper";
import { CheckIcon, ClockIcon, WarningIcon } from "./BulkSendIcons";

const p = tokens.palette;
const t = tokens.roles;
const s = rolesStyles;
/**
 * "Deliver via" is the create-request flow's own control, borrowed whole: the
 * same chip row, the same tick, and the same split phone field beside it. A
 * role's delivery should not look like a different idea here.
 */
const a = approversAndSignersStyles;

interface Props {
  assignments: BulkRoleAssignment[];
  errors: BulkRoleErrors;
  formError?: string;
  /** Rows imported so far, for the "same person on all N copies" copy. */
  recipientCount: number;
  onChangeMode: (signerIndex: number, mode: BulkRoleMode) => void;
  onChangeField: (
    signerIndex: number,
    field: "name" | "email" | "phone" | "accessCode",
    value: string,
  ) => void;
  onToggleChannel: (signerIndex: number, channel: BulkDeliveryChannel) => void;
  onChangeSecurity: (signerIndex: number, security: BulkSecurityKind) => void;
  onChangeDelay: (
    signerIndex: number,
    field: "delayDays" | "delayHours",
    value: number,
  ) => void;
}

/** Order and copy follow the single-send flow's own security dropdown. */
const SECURITY_META: { value: BulkSecurityKind; caption: string }[] = [
  { value: "none", caption: "Anyone with the link can sign" },
  { value: "otp", caption: "One-time code sent to the signer's email" },
  { value: "sms", caption: strings.smsDelivery.securityPhoneCaption },
  { value: "code", caption: "A code you set and share another way" },
  ...IDV_SECURITY_OPTIONS.map((option) => ({
    value: option.key,
    caption: option.caption,
  })),
];

const BulkRolesStep: React.FC<Props> = ({
  assignments,
  errors,
  formError,
  recipientCount,
  onChangeMode,
  onChangeField,
  onToggleChannel,
  onChangeSecurity,
  onChangeDelay,
}) => {
  /* Same gates a single send is subject to: every row becomes one envelope. */
  const gates = useBulkGates();
  const idvOptions = useIdvSecurityOptions();
  /** Which role's security menu is open, by slot id. */
  const [securityMenu, setSecurityMenu] = useState<{
    signerIndex: number;
    anchor: HTMLElement;
  } | null>(null);

  /** Which security options this plan may pick, and why not. */
  const securityGate = (
    kind: BulkSecurityKind,
  ): { locked: boolean; tooltip: string } => {
    if (kind === "otp") {
      return {
        locked: !gates.authOtp.available,
        tooltip: gates.authOtp.tooltip,
      };
    }
    if (kind === "code") {
      return {
        locked: !gates.authPasscode.available,
        tooltip: gates.authPasscode.tooltip,
      };
    }
    if (kind === "sms") {
      return {
        locked: !gates.authPhone.available,
        tooltip: gates.authPhone.tooltip,
      };
    }
    return { locked: false, tooltip: "" };
  };
  const fixedCount = assignments.filter((a) => a.mode === "fixed").length;
  const varyCount = assignments.length - fixedCount;
  /* Until a list exists the count is unknown, so the copy stays unnumbered. */
  const copiesLabel =
    recipientCount > 0
      ? `all ${recipientCountLabel(recipientCount)}`
      : "every copy";
  const requestsLabel =
    recipientCount > 0
      ? `${recipientCount} separate requests`
      : "one request per recipient";
  /** The role whose column drives the batch, for "signs after each ___". */
  const varyingLabel =
    assignments.find((role) => role.mode === "vary")?.slot.label ?? "recipient";

  /** The wait a level is holding, read off the first role that carries it. */
  const levelDelay = (order: number) => {
    const owner = assignments.find(
      (role) =>
        role.slot.order === order &&
        !role.slot.cc &&
        (role.delayDays > 0 || role.delayHours > 0),
    );
    return { days: owner?.delayDays ?? 0, hours: owner?.delayHours ?? 0 };
  };

  /** A wait applies to the whole level, so every role in it is written. */
  const setLevelDelay = (order: number, days: number, hours: number) =>
    assignments
      .filter((role) => role.slot.order === order && !role.slot.cc)
      .forEach((role) => {
        onChangeDelay(role.slot.signerIndex, "delayDays", days);
        onChangeDelay(role.slot.signerIndex, "delayHours", hours);
      });

  return (
    <Box sx={bulkShellStyles.stepPage("list")}>
      <Typography component="h1" sx={bulkShellStyles.pageTitle}>
        Signers
      </Typography>
      <Typography component="p" sx={bulkShellStyles.pageSub}>
        {varyCount === 1
          ? "Exactly one role varies"
          : `${varyCount} roles vary`}{" "}
        per recipient — that's the column your list fills. Every other role is
        the same person on {copiesLabel}.
      </Typography>

      <Box sx={s.rows}>
        {assignments.map((assignment, index) => {
          const { slot } = assignment;
          const varies = assignment.mode === "vary";
          const rowErrors = errors[slot.signerIndex] || {};
          const color = roleColor(slot.signerIndex);
          /*
            A wait sits between two levels, and is stored on the level that
            runs after it — the same place the create-request flow keeps it.
          */
          const nextOrder = assignments[index + 1]?.slot.order;
          const startsNextLevel =
            nextOrder !== undefined && nextOrder !== slot.order;

          return (
            <React.Fragment key={slot.signerIndex}>
              <Box sx={s.row(varies)}>
                <Box sx={s.rowInner}>
                  <Box sx={s.accentBar(color)} />
                  <Box sx={s.avatar(color)}>{roleInitials(slot.label)}</Box>

                  <Box sx={s.identity}>
                    <Box component="span" sx={s.roleName}>
                      {slot.label}
                      {slot.cc ? " · receives a copy" : ""}
                    </Box>
                    <Box component="span" sx={s.fieldNote}>
                      {slot.cc ? "Does not sign" : fieldNote(slot)}
                    </Box>
                  </Box>

                  <Box
                    sx={s.toggle}
                    role="group"
                    aria-label={`How ${slot.label} is filled`}
                  >
                    <Box
                      component="button"
                      type="button"
                      aria-pressed={varies}
                      onClick={() => onChangeMode(slot.signerIndex, "vary")}
                      sx={s.toggleItem(varies, true)}
                    >
                      Varies per recipient
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      aria-pressed={!varies}
                      onClick={() => onChangeMode(slot.signerIndex, "fixed")}
                      sx={s.toggleItem(!varies, false)}
                    >
                      Same for all
                    </Box>
                  </Box>

                  {!slot.cc && (
                    <Box sx={s.inlineGroup}>
                      <Box component="span" sx={s.inlineLabel}>
                        Verify with
                      </Box>
                      <Button
                        disableRipple
                        aria-haspopup="true"
                        aria-expanded={
                          securityMenu?.signerIndex === slot.signerIndex
                            ? "true"
                            : undefined
                        }
                        onClick={(event: React.MouseEvent<HTMLElement>) =>
                          setSecurityMenu({
                            signerIndex: slot.signerIndex,
                            anchor: event.currentTarget,
                          })
                        }
                        sx={a.select}
                      >
                        <ShieldGlyph
                          sx={a.selectShield(assignment.security !== "none")}
                        />
                        {BULK_SECURITY_LABELS[assignment.security]}
                        <CaretDownGlyph sx={a.selectCaret} />
                      </Button>
                    </Box>
                  )}
                </Box>

                {/*
                Who fills the role, on a line of its own. A varying role has no
                such line: its people come from the recipient list in step 4.
              */}
                {!varies && (
                  <Box sx={s.fillRow}>
                    <Box sx={s.fixedInputs}>
                      <Box sx={s.roleField(1, t.inputNameMinWidth)}>
                        <Box
                          component="input"
                          id={`role-name-${slot.signerIndex}`}
                          placeholder="Enter Name"
                          value={assignment.name}
                          aria-label={`${slot.label} name`}
                          aria-invalid={Boolean(rowErrors.name)}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) =>
                            onChangeField(
                              slot.signerIndex,
                              "name",
                              event.target.value,
                            )
                          }
                          sx={s.roleInput(Boolean(rowErrors.name))}
                        />
                        {rowErrors.name && (
                          <Box component="span" sx={s.roleFieldMessage}>
                            {rowErrors.name}
                          </Box>
                        )}
                      </Box>

                      <Box sx={s.roleField(1.2, t.inputEmailMinWidth)}>
                        <Box
                          component="input"
                          type="email"
                          id={`role-email-${slot.signerIndex}`}
                          placeholder="Enter Email"
                          value={assignment.email}
                          aria-label={`${slot.label} email`}
                          aria-invalid={Boolean(rowErrors.email)}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) =>
                            onChangeField(
                              slot.signerIndex,
                              "email",
                              event.target.value,
                            )
                          }
                          sx={s.roleInput(Boolean(rowErrors.email))}
                        />
                        {rowErrors.email && (
                          <Box component="span" sx={s.roleFieldMessage}>
                            {rowErrors.email}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}

                {!slot.cc && (
                  <Box sx={s.deliverRow}>
                    <Box sx={a.deliverViaLine}>
                      <Box component="span" sx={a.deliverViaLabel}>
                        {strings.smsDelivery.deliverViaLabel}
                      </Box>
                      {BULK_CHANNEL_OPTIONS.map((channel) => {
                        const checked = assignment.channels.includes(channel);
                        const locked =
                          channel !== "EMAIL" && !gates.sendSms.available;
                        const option = (
                          <Button
                            disableRipple
                            disabled={locked}
                            aria-pressed={checked}
                            onClick={() =>
                              onToggleChannel(slot.signerIndex, channel)
                            }
                            sx={a.deliverOption}
                          >
                            <Box component="span" sx={a.deliverBox(checked)}>
                              <CheckIcon size={9} color={p.white} />
                            </Box>
                            <Box component="span" sx={a.deliverOptionLabel}>
                              {BULK_CHANNEL_LABELS[channel]}
                            </Box>
                            {locked && <LockOutlinedIcon sx={a.deliverLock} />}
                          </Button>
                        );

                        return locked && gates.sendSms.tooltip ? (
                          <Tooltip
                            describeChild
                            key={channel}
                            title={gates.sendSms.tooltip}
                            placement="top"
                            arrow
                          >
                            <Box
                              component="span"
                              sx={{ display: "inline-flex" }}
                            >
                              {option}
                            </Box>
                          </Tooltip>
                        ) : (
                          <React.Fragment key={channel}>
                            {option}
                          </React.Fragment>
                        );
                      })}
                    </Box>

                    {/*
                      Beside the channels, exactly as the normal flow has it —
                      and shown whenever the number is used at all, whether
                      that is Text delivery, an SMS code, or both sharing the
                      one number.
                    */}
                    {!varies && roleNeedsPhone(assignment) && (
                      <Box sx={s.inlineGroup}>
                        <Box component="span" sx={s.inlineLabel}>
                          Phone
                        </Box>
                        <Box sx={s.phoneWrap}>
                          <CustomContactNumberInput
                            id={`role-${slot.signerIndex}-phone`}
                            placeHolder={strings.smsDelivery.phonePlaceholder}
                            value={assignment.phone}
                            countries={strings.SMS_SUPPORTED_COUNTRIES}
                            onChange={(value: string) =>
                              onChangeField(slot.signerIndex, "phone", value)
                            }
                          />
                        </Box>
                      </Box>
                    )}

                    {/*
                      What this row's choice costs two steps later. It shares
                      the row rather than taking one of its own.
                    */}
                    {varies && roleNeedsPhone(assignment) && (
                      <Box sx={s.deliverNotice}>
                        <WarningIcon
                          size={t.noticeIconSize}
                          color={p.warnText}
                        />
                        <Box component="span">
                          Your recipient list now needs a <b>phone</b> column —
                          it becomes required on the Recipients step.
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/*
                An access code is a secret per person, so it is captured where
                that person is named: here for a pinned role, and — for a role
                that varies — as a column of the recipient list, which the chip
                above already names.
              */}
                {!slot.cc && assignment.security === "code" && !varies && (
                  <Box sx={s.deliverRow}>
                    <Box component="span" sx={s.inlineLabel}>
                      Access code
                    </Box>
                    <Box sx={s.roleField(0)}>
                      <Box
                        component="input"
                        id={`role-code-${slot.signerIndex}`}
                        placeholder="Code you share with this signer"
                        value={assignment.accessCode}
                        autoComplete="off"
                        maxLength={BULK_ACCESS_CODE_MAX}
                        aria-label={`${slot.label} access code`}
                        aria-invalid={Boolean(rowErrors.code)}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) =>
                          onChangeField(
                            slot.signerIndex,
                            "accessCode",
                            event.target.value,
                          )
                        }
                        sx={[
                          s.roleInput(Boolean(rowErrors.code)),
                          { width: t.codeInputWidth },
                        ]}
                      />
                      {rowErrors.code && (
                        <Box component="span" sx={s.roleFieldMessage}>
                          {rowErrors.code}
                        </Box>
                      )}
                    </Box>
                    <Box component="span" sx={s.fieldNote}>
                      Never emailed — share it with them yourself.
                    </Box>
                  </Box>
                )}

                {securityMenu?.signerIndex === slot.signerIndex && (
                  <Menu
                    anchorEl={securityMenu.anchor}
                    open
                    onClose={() => setSecurityMenu(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    MenuListProps={{ sx: { p: 0 } }}
                    PaperProps={{
                      sx: a.menuPaper(
                        recipientsTokens.select.menuWidthSecurity,
                      ),
                    }}
                  >
                    {SECURITY_META.filter(
                      // Identity verification: only what useIdvSecurityOptions
                      // offers, plus this role's own current choice.
                      (meta) =>
                        !isIdvSecurityKey(meta.value) ||
                        assignment.security === meta.value ||
                        idvOptions.some((option) => option.key === meta.value),
                    ).map((meta) => {
                      const gate = securityGate(meta.value);
                      const item = (
                        <MenuItem
                          key={meta.value}
                          selected={assignment.security === meta.value}
                          disabled={gate.locked}
                          onClick={() => {
                            onChangeSecurity(slot.signerIndex, meta.value);
                            setSecurityMenu(null);
                          }}
                          sx={a.menuItem}
                        >
                          <Typography component="div" sx={a.menuItemTitle}>
                            {BULK_SECURITY_LABELS[meta.value]}
                            {gate.locked && (
                              <LockOutlinedIcon
                                sx={{
                                  fontSize: 13,
                                  color: "text.disabled",
                                  ml: 0.75,
                                }}
                              />
                            )}
                          </Typography>
                          <Typography component="div" sx={a.menuItemSub}>
                            {meta.caption}
                          </Typography>
                        </MenuItem>
                      );

                      return gate.locked && gate.tooltip ? (
                        <Tooltip
                          describeChild
                          key={meta.value}
                          title={gate.tooltip}
                          placement="left"
                          arrow
                        >
                          <Box component="span">{item}</Box>
                        </Tooltip>
                      ) : (
                        item
                      );
                    })}
                  </Menu>
                )}

                {!varies && slot.order > 1 && (
                  <Box sx={s.orderNote}>
                    <ClockIcon size={t.orderNoteIconSize} color={p.muted} />
                    <Box component="span">
                      Signs after each {varyingLabel.toLowerCase()} completes —{" "}
                      {requestsLabel} will reach{" "}
                      {assignment.name.trim().split(" ")[0] || slot.label} over
                      the batch.
                    </Box>
                  </Box>
                )}
              </Box>

              {startsNextLevel && (
                <BulkDelaySlot
                  days={levelDelay(nextOrder!).days}
                  hours={levelDelay(nextOrder!).hours}
                  disabled={!gates.delayedRouting.available}
                  tooltip={gates.delayedRouting.tooltip}
                  levelLabel={`level ${nextOrder}`}
                  onChange={(days, hours) =>
                    setLevelDelay(nextOrder!, days, hours)
                  }
                  onRemove={() => setLevelDelay(nextOrder!, 0, 0)}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {formError && (
        <Typography component="p" sx={s.formError} role="alert">
          {formError}
        </Typography>
      )}

      {fixedCount > 0 && (
        <Box sx={s.warning}>
          <WarningIcon size={t.warningIconSize} color={p.warnText} />
          <Box component="span">
            A fixed signer receives <b>{requestsLabel}</b> — one per recipient.
            Consider a delay between levels so they aren't all delivered at
            once; they can work through them from the batch's signing queue.
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BulkRolesStep;
